import { toast as sonnerToast, type ExternalToast } from 'sonner'

/**
 * Calculate toast duration based on message length.
 * ~200ms per character with min 3s, max 10s.
 */
function calcDuration(message: string | undefined): number {
  if (!message) return 4000
  const len = typeof message === 'string' ? message.length : 0
  // ~80ms per char, base 3000ms
  const ms = Math.max(3000, Math.min(10000, 3000 + len * 80))
  return ms
}

function withDuration(message: string | React.ReactNode, data?: ExternalToast): ExternalToast {
  const text = typeof message === 'string' ? message : undefined
  return { duration: calcDuration(text), ...data }
}

// Re-export a toast object that auto-scales duration
// but lets callers override with an explicit duration
export const toast = Object.assign(
  (message: string | React.ReactNode, data?: ExternalToast) =>
    sonnerToast(message, withDuration(message, data)),
  {
    success: (message: string | React.ReactNode, data?: ExternalToast) =>
      sonnerToast.success(message, withDuration(message, data)),
    error: (message: string | React.ReactNode, data?: ExternalToast) =>
      sonnerToast.error(message, withDuration(message, data)),
    info: (message: string | React.ReactNode, data?: ExternalToast) =>
      sonnerToast.info(message, withDuration(message, data)),
    warning: (message: string | React.ReactNode, data?: ExternalToast) =>
      sonnerToast.warning(message, withDuration(message, data)),
    message: (message: string | React.ReactNode, data?: ExternalToast) =>
      sonnerToast.message(message, withDuration(message, data)),
    loading: sonnerToast.loading,
    promise: sonnerToast.promise,
    dismiss: sonnerToast.dismiss,
    custom: sonnerToast.custom,
  },
)
